import React, { useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      console.log('Contact query submitted:', response.data);
      setSubmitStatus({
        type: 'success',
        message: 'Thank you! Your query has been submitted successfully. We will contact you soon.',
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: '',
      });
    } catch (error) {
      console.error('Error submitting contact query:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Failed to submit your query. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <div className="animated-bg"></div>

      {/* Hero Section */}
      <section className="pt-24 pb-14 px-4" data-testid="contact-hero">
        <div className="max-w-6xl mx-auto text-center">
          <div className="fade-in">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Get in <span className="neon-text">Touch</span>
            </h1>
            <p className="text-base text-white/80 max-w-2xl mx-auto">
              Have a question or need a quote? We're here to help you find the perfect water
              treatment solution.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form and Info */}
      <section className="pb-14 px-4" data-testid="contact-form-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Contact Form */}
            <div className="slide-in-left">
              <div className="glass-card rounded-2xl p-6" data-testid="contact-form">
                <h2 className="text-xl font-bold text-white mb-4">Send us a Message</h2>

                {submitStatus && (
                  <div
                    className={`mb-6 p-4 rounded-lg ${
                      submitStatus.type === 'success'
                        ? 'bg-green-500/20 border border-green-500/50'
                        : 'bg-red-500/20 border border-red-500/50'
                    }`}
                    data-testid="submit-status-message"
                  >
                    <p className="text-white">{submitStatus.message}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-white mb-1 text-sm font-medium">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 rounded-lg text-sm"
                      placeholder="Full Name"
                      data-testid="contact-name-input"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-white mb-1 text-sm font-medium">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 rounded-lg text-sm"
                      placeholder="Email"
                      data-testid="contact-email-input"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-white mb-1 text-sm font-medium">
                      Phone *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 rounded-lg text-sm"
                      placeholder="Phone Number"
                      data-testid="contact-phone-input"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-white mb-1 text-sm font-medium">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="4"
                      className="w-full px-3 py-2 rounded-lg resize-none text-sm"
                      placeholder="Tell us about your project requirements..."
                      data-testid="contact-message-input"
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full neon-button px-6 py-3 rounded-full text-white font-semibold text-sm ${
                      isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              </div>
            </div>

            {/* Contact Information */}
            <div className="slide-in-right space-y-4">
              {/* Contact Details */}
              <div className="glass-card rounded-2xl p-6" data-testid="contact-info">
                <h2 className="text-xl font-bold text-white mb-4">Contact Information</h2>
                <div className="space-y-4">
                  <div className="flex items-start" data-testid="contact-address">
                    <div className="text-xl mr-3">📍</div>
                    <div>
                      <h3 className="text-base font-semibold text-white mb-1">Address : </h3>
                      <p className="text-sm text-white/80">M.P Nagar, zone 1, Bhopal, Madhya Pradesh, 462024
                        
    
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start" data-testid="contact-phone-info">
                    <div className="text-xl mr-3">📞</div>
                    <div>
                      <h3 className="text-base font-semibold text-white mb-1">Phone</h3>
                      <p className="text-sm text-white/80">Water Treatment and ETP: +91-9893561108 </p>
                      <p className="text-sm text-white/80">Engineering Services: +91-7222931035 </p>
                    </div>
                  </div>

                  <div className="flex items-start" data-testid="contact-email-info">
                    <div className="text-xl mr-3">📧</div>
                    <div>
                      <h3 className="text-base font-semibold text-white mb-1">Email</h3>
                      <p className="text-sm text-white/80">info@researchandSolution.com</p>
                      <p className="text-sm text-white/80">sales@researchandSolution.com</p>
                    </div>
                  </div>

                  <div className="flex items-start" data-testid="contact-hours">
                    <div className="text-xl mr-3">🕐</div>
                    <div>
                      <h3 className="text-base font-semibold text-white mb-1">Business Hours</h3>
                      <p className="text-sm text-white/80">Monday - Friday: 9:00 AM - 6:00 PM</p>
                      <p className="text-sm text-white/80">Saturday: 10:00 AM - 4:00 PM</p>
                      <p className="text-sm text-white/80">Sunday: Closed</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Response */}
              <div className="glass-card rounded-2xl p-6" data-testid="quick-response">
                <h3 className="text-lg font-bold text-white mb-2">Quick Response</h3>
                <p className="text-sm text-white/90 mb-3">
                  We typically respond to all inquiries within 24 hours during business days.
                </p>
                <div className="flex items-center space-x-2">
                  <div className="w-2.5 h-2.5 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="text-sm text-white/80">Available now</span>
                </div>
              </div>

              {/* Emergency Contact */}
              <div className="glass-card rounded-2xl p-6 border-2 border-purple-400/50" data-testid="emergency-contact">
                <h3 className="text-lg font-bold text-white mb-2">
                  <span className="neon-text">24/7 Support</span>
                </h3>
                <p className="text-sm text-white/90 mb-3">
                  For urgent technical support, our team is available round the
                  clock.
                </p>
                <a
                  href="tel:+15551234567"
                  className="neon-button px-5 py-2 rounded-full text-white text-sm font-semibold inline-block"
                  data-testid="emergency-call-btn"
                >
                  Call Emergency Line
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="glass py-6 px-4 mt-14">
        <div className="max-w-6xl mx-auto text-center text-sm text-white/80">
          <p>&copy; 2025 Research and Solution. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Contact;
